import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast, Toaster } from "react-hot-toast";
import { WorkspaceScenarioWithEvaluation } from "winslow-core";
import {
  getWorkspaceScenarioEvaluations,
  submitWorkspaceScenarioEvaluations,
} from "../../services/WorkspaceEvaluationsService";
import "./WorkspaceEvaluationPage.css";

type ScenarioResponse = {
  pass: boolean | null;
  note: string;
};

const defaultScenarioState: ScenarioResponse = { pass: null, note: "" };

const buildScenarioState = (
  scenarios: WorkspaceScenarioWithEvaluation[]
): Record<string, ScenarioResponse> => {
  return scenarios.reduce<Record<string, ScenarioResponse>>((acc, scenario) => {
    acc[scenario.id] = {
      pass:
        typeof scenario.evaluation?.pass === "boolean"
          ? scenario.evaluation.pass
          : null,
      note: scenario.evaluation?.note ?? "",
    };
    return acc;
  }, {});
};

export default function WorkspaceEvaluationPage() {
  const [searchParams] = useSearchParams();
  const workspaceId =
    searchParams.get("workspace_id") || searchParams.get("workspaceId") || "";
  const email = searchParams.get("email") || "";

  const [scenarios, setScenarios] = useState<WorkspaceScenarioWithEvaluation[]>(
    []
  );
  const [responses, setResponses] = useState<Record<string, ScenarioResponse>>(
    {}
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savingStates, setSavingStates] = useState<Record<string, boolean>>({});
  const statusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const noteDebounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {}
  );

  useEffect(() => {
    const loadScenarios = async () => {
      if (!workspaceId || !email) {
        setError("Workspace and email must be provided in the link.");
        return;
      }

      setLoading(true);
      setError(null);
      const result = await getWorkspaceScenarioEvaluations(workspaceId, email);

      if (!result) {
        setError("We couldn't load the scenarios. Please try again later.");
        setScenarios([]);
        setResponses({});
      } else {
        setScenarios(result);
        setResponses(buildScenarioState(result));
      }

      setLoading(false);
    };

    loadScenarios();
  }, [workspaceId, email]);

  const answeredCount = useMemo(() => {
    return scenarios.filter(
      (scenario) => typeof responses[scenario.id]?.pass === "boolean"
    ).length;
  }, [responses, scenarios]);

  const persistEvaluation = useCallback(
    async (scenarioId: string, payload: ScenarioResponse) => {
      if (!workspaceId || !email || typeof payload.pass !== "boolean") {
        return;
      }

      setSavingStates((prev) => ({ ...prev, [scenarioId]: true }));
      setSaveError(null);

      const result = await submitWorkspaceScenarioEvaluations({
        workspaceId,
        email,
        evaluations: [
          {
            scenarioId,
            pass: payload.pass,
            note: payload.note?.trim() || undefined,
          },
        ],
      });

      setSavingStates((prev) => ({ ...prev, [scenarioId]: false }));

      if (!result || !result.length) {
        setSaveError("We couldn't save the latest changes. Please retry.");
        return;
      }

      const updatedEvaluation = result.find(
        (item) => item.scenarioId === scenarioId
      );

      if (updatedEvaluation) {
        setScenarios((prev) =>
          prev.map((scenario) =>
            scenario.id === scenarioId
              ? { ...scenario, evaluation: updatedEvaluation }
              : scenario
          )
        );
      }

      toast.success("Saved", { id: `scenario-${scenarioId}` });
    },
    [email, workspaceId]
  );

  const setScenarioResponse = (
    scenarioId: string,
    partial: Partial<ScenarioResponse>,
    saveStrategy?: "immediate" | "debounced"
  ) => {
    const previous = responses[scenarioId] ?? defaultScenarioState;
    const nextResponse: ScenarioResponse = {
      ...previous,
      ...partial,
    };

    setResponses((prev) => ({ ...prev, [scenarioId]: nextResponse }));

    if (saveStrategy === "immediate") {
      persistEvaluation(scenarioId, nextResponse);
    } else if (saveStrategy === "debounced") {
      const existingTimeout = noteDebounceRef.current[scenarioId];
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      noteDebounceRef.current[scenarioId] = setTimeout(() => {
        persistEvaluation(scenarioId, nextResponse);
      }, 800);
    }
  };

  const handlePassChange = (scenarioId: string, pass: boolean) => {
    setScenarioResponse(scenarioId, { pass }, "immediate");
  };

  const handleNoteChange = (scenarioId: string, note: string) => {
    setScenarioResponse(scenarioId, { note }, "debounced");
  };

  useEffect(() => {
    return () => {
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
      Object.values(noteDebounceRef.current).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
    };
  }, []);

  const renderScenario = (
    scenario: WorkspaceScenarioWithEvaluation,
    isSaving?: boolean
  ) => (
    <div className="workspace-eval-card" key={scenario.id}>
      <div className="workspace-eval-card__meta">
        <span>Persona: {scenario.persona}</span>
        <span>Owner: {scenario.owner}</span>
      </div>
      <div className="workspace-eval-card__tags">
        <span className="workspace-eval-tag">{scenario.category}</span>
        {scenario.shouldEscalate && (
          <span className="workspace-eval-tag is-alert">Escalate</span>
        )}
        {scenario.shouldClarify && (
          <span className="workspace-eval-tag is-info">Clarify</span>
        )}
        {scenario.shouldFail && (
          <span className="workspace-eval-tag is-alert">Should Fail</span>
        )}
      </div>
      <h3 className="workspace-eval-question">{scenario.question}</h3>
      <div className="workspace-eval-answer">
        <strong>Expected Answer</strong>
        <p>{scenario.answer}</p>
      </div>
      {scenario.documentsReturned && (
        <div className="workspace-eval-documents">
          <strong>Documents:</strong> {scenario.documentsReturned}
        </div>
      )}
      <div className="workspace-eval-controls">
        <div className="workspace-eval-pill-group">
          <button
            type="button"
            className={`workspace-eval-pill is-pass ${
              responses[scenario.id]?.pass === true ? "is-active" : ""
            }`}
            onClick={() => handlePassChange(scenario.id, true)}
            disabled={isSaving}>
            Pass
          </button>
          <button
            type="button"
            className={`workspace-eval-pill is-fail ${
              responses[scenario.id]?.pass === false ? "is-active" : ""
            }`}
            onClick={() => handlePassChange(scenario.id, false)}
            disabled={isSaving}>
            Fail
          </button>
        </div>
        <label>
          <span>Optional Feedback</span>
          <textarea
            className="workspace-eval-textarea"
            placeholder="Let us know what worked well or what needs attention."
            value={responses[scenario.id]?.note ?? ""}
            onChange={(event) =>
              handleNoteChange(scenario.id, event.target.value)
            }
            disabled={isSaving}
          />
        </label>
        <div className="workspace-eval-status">
          {isSaving
            ? "Saving..."
            : typeof responses[scenario.id]?.pass === "boolean"
              ? "Saved"
              : "Awaiting review"}
        </div>
      </div>
    </div>
  );

  return (
    <div className="workspace-eval-page">
      <div className="workspace-eval-container">
        <div className="workspace-eval-header">
          <h1>Workspace Evaluation</h1>
          <div className="workspace-eval-badge">
            {answeredCount}/{scenarios.length || 0} reviewed
          </div>
          <p>
            Please review each scenario, marking it as a pass or fail and adding
            context where helpful. Your feedback goes straight to the Winslow
            team supporting this workspace.
          </p>
          <span className="workspace-eval-helper">
            Your selections and notes save automatically.
          </span>
        </div>

        {error && <div className="workspace-eval-error">{error}</div>}
        {saveError && <div className="workspace-eval-error">{saveError}</div>}
        {loading && (
          <div className="workspace-eval-loading">Loading scenarios...</div>
        )}
        {!loading && scenarios.length === 0 && !error && (
          <div className="workspace-eval-empty">
            There aren't any scenarios to review for this workspace yet.
          </div>
        )}

        {scenarios.length > 0 && (
          <div className="workspace-eval-list">
            {scenarios.map((scenario) =>
              renderScenario(scenario, savingStates[scenario.id])
            )}
          </div>
        )}
      </div>
      <Toaster position="bottom-right" />
    </div>
  );
}
